<template>
  <div class="tab-card">

    <div class="tab-header">
      <div class="tab-header-left">
        <i class="ti ti-sparkles" aria-hidden="true"></i>
        <span>Absenteeism trend alert</span>
      </div>
      <span class="tab-sub">Last 90 days · Live data</span>
    </div>

    <!-- Summary metrics -->
    <div class="tab-metrics">
      <div class="tab-metric">
        <div class="tab-val" :style="{ color: counts.high > 0 ? '#A32D2D' : 'inherit' }">{{ counts.high }}</div>
        <div class="tab-label">High risk</div>
      </div>
      <div class="tab-metric">
        <div class="tab-val" :style="{ color: counts.medium > 0 ? '#854F0B' : 'inherit' }">{{ counts.medium }}</div>
        <div class="tab-label">Medium risk</div>
      </div>
      <div class="tab-metric">
        <div class="tab-val" style="color:#27500A">{{ counts.low }}</div>
        <div class="tab-label">Low risk</div>
      </div>
      <div class="tab-metric">
        <div class="tab-val">{{ data.length }}</div>
        <div class="tab-label">Total flagged</div>
      </div>
    </div>

    <!-- Staff list -->
    <div class="tab-list">
      <div
        v-for="(staff, i) in visibleStaff"
        :key="i"
        class="tab-row"
      >
        <!-- Avatar -->
        <div class="tab-avatar" :style="avatarStyle(staff.risk_level)">
          {{ initials(staff.psnname) }}
        </div>

        <!-- Info -->
        <div class="tab-info">
          <div class="tab-name">{{ staff.psnname }}</div>
          <div class="tab-dept">{{ staff.deptname }} · {{ staff.tenure_years }}yrs</div>
          <!-- Signal pills -->
          <div class="tab-signals">
            <span v-if="staff.absence_rate_pct >= 10" class="tab-sig tab-sig-red">
              {{ staff.absence_rate_pct }}% absent
            </span>
            <span v-if="staff.mon_fri_ratio >= 50" class="tab-sig tab-sig-amber">
              Mon/Fri pattern
            </span>
            <span v-if="staff.trend_delta_pct > 20" class="tab-sig tab-sig-amber">
              ↑ trending up
            </span>
            <span v-if="staff.unexplained_count >= 2" class="tab-sig tab-sig-red">
              {{ staff.unexplained_count }} unexplained
            </span>
            <span v-if="staff.missing_clockout >= 3" class="tab-sig tab-sig-blue">
              {{ staff.missing_clockout }} no clockout
            </span>
          </div>
        </div>

        <!-- Score -->
        <div class="tab-score-wrap">
          <div class="tab-bar-bg">
            <div class="tab-bar-fill"
                 :style="{ width: staff.risk_score + '%', background: scoreColor(staff.risk_score) }">
            </div>
          </div>
          <div class="tab-score-num" :style="{ color: scoreColor(staff.risk_score) }">
            {{ staff.risk_score }}
          </div>
        </div>

        <!-- Badge -->
        <span class="tab-badge" :class="badgeClass(staff.risk_level)">
          {{ staff.risk_level }}
        </span>
      </div>

      <div v-if="data.length === 0" class="tab-empty">
        No absenteeism patterns detected in the last 90 days.
      </div>
    </div>

    <!-- Show more -->
    <div v-if="data.length > 8" class="tab-more">
      <button class="tab-more-btn" @click="showAll = !showAll">
        {{ showAll ? 'Show less ↑' : 'Show all ' + data.length + ' staff ↓' }}
      </button>
    </div>

    <!-- Footer -->
    <div class="tab-footer">
      <button class="tab-btn" @click="askMore('Which staff have the worst absenteeism patterns and what actions should I take?')">
        Action plan ↗
      </button>
      <button class="tab-btn" @click="askMore('Is there a correlation between absenteeism and attrition risk in my team?')">
        Link to attrition ↗
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: "TaraAbsenteeismCard",
  props: { data: { type: Array, required: true } },
  data() { return { showAll: false }; },
  computed: {
    counts() {
      return {
        high:   this.data.filter(s => s.risk_level === 'HIGH').length,
        medium: this.data.filter(s => s.risk_level === 'MEDIUM').length,
        low:    this.data.filter(s => s.risk_level === 'LOW').length,
      };
    },
    visibleStaff() {
      return this.showAll ? this.data : this.data.slice(0, 8);
    },
  },
  methods: {
    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    },
    avatarStyle(level) {
      if (level === 'HIGH')   return { background: '#FCEBEB', color: '#791F1F' };
      if (level === 'MEDIUM') return { background: '#FAEEDA', color: '#633806' };
      return { background: '#EAF3DE', color: '#27500A' };
    },
    scoreColor(score) {
      if (score >= 70) return '#A32D2D';
      if (score >= 40) return '#854F0B';
      return '#3B6D11';
    },
    badgeClass(level) {
      if (level === 'HIGH')   return 'tab-badge-high';
      if (level === 'MEDIUM') return 'tab-badge-med';
      return 'tab-badge-low';
    },
    askMore(text) { this.$emit('ask', text); },
  },
};
</script>

<style scoped>
.tab-card { border:0.5px solid #AFA9EC; border-radius:12px; background:#FAFAFE; overflow:hidden; font-family:inherit; font-size:13px; margin:4px 0 8px; max-width:440px; }
.tab-header { display:flex; align-items:center; justify-content:space-between; padding:10px 14px 8px; background:#EEEDFE; border-bottom:0.5px solid #AFA9EC; }
.tab-header-left { display:flex; align-items:center; gap:6px; font-weight:500; color:#3C3489; font-size:13px; }
.tab-header-left i { font-size:15px; }
.tab-sub { font-size:11px; color:#534AB7; }
.tab-metrics { display:grid; grid-template-columns:repeat(4,1fr); border-bottom:0.5px solid #E5E3F8; }
.tab-metric { padding:10px 8px; text-align:center; border-right:0.5px solid #E5E3F8; }
.tab-metric:last-child { border-right:none; }
.tab-val { font-size:20px; font-weight:500; color:#26215C; }
.tab-label { font-size:10px; color:#7F77DD; margin-top:2px; }
.tab-list { padding:4px 0; }
.tab-row { display:flex; align-items:flex-start; gap:8px; padding:8px 14px; border-bottom:0.5px solid #F0EFF9; }
.tab-row:last-child { border-bottom:none; }
.tab-avatar { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:500; flex-shrink:0; margin-top:2px; }
.tab-info { flex:1; min-width:0; }
.tab-name { font-size:12px; font-weight:500; color:#26215C; }
.tab-dept { font-size:10px; color:#7F77DD; margin-top:1px; }
.tab-signals { display:flex; flex-wrap:wrap; gap:3px; margin-top:4px; }
.tab-sig { font-size:10px; font-weight:500; padding:1px 6px; border-radius:20px; }
.tab-sig-red   { background:#FCEBEB; color:#791F1F; }
.tab-sig-amber { background:#FAEEDA; color:#633806; }
.tab-sig-blue  { background:#E6F1FB; color:#0C447C; }
.tab-score-wrap { display:flex; flex-direction:column; align-items:flex-end; gap:3px; width:56px; flex-shrink:0; padding-top:2px; }
.tab-bar-bg { width:100%; height:4px; background:#E5E3F8; border-radius:2px; overflow:hidden; }
.tab-bar-fill { height:100%; border-radius:2px; }
.tab-score-num { font-size:10px; font-weight:500; }
.tab-badge { font-size:10px; font-weight:500; padding:2px 6px; border-radius:20px; flex-shrink:0; white-space:nowrap; margin-top:2px; }
.tab-badge-high { background:#FCEBEB; color:#791F1F; }
.tab-badge-med  { background:#FAEEDA; color:#633806; }
.tab-badge-low  { background:#EAF3DE; color:#27500A; }
.tab-empty { padding:16px 14px; font-size:12px; color:#7F77DD; text-align:center; }
.tab-more { padding:4px 14px 8px; text-align:center; }
.tab-more-btn { font-size:11px; color:#7F77DD; background:none; border:none; cursor:pointer; }
.tab-footer { display:flex; gap:8px; padding:10px 14px; background:#F5F4FD; border-top:0.5px solid #E5E3F8; }
.tab-btn { font-size:12px; padding:5px 10px; border-radius:6px; border:0.5px solid #AFA9EC; background:white; color:#3C3489; cursor:pointer; }
.tab-btn:hover { background:#EEEDFE; }
</style>
